import React, { useState, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import { ArrowLeft, ArrowRight } from 'lucide-react';

import fssaiImg from "../../../assets/Images/certificates/fssai.png";
import haccpImg from "../../../assets/Images/certificates/haccp.png";
import halalImg from "../../../assets/Images/certificates/halal.png";
import isoImg from "../../../assets/Images/certificates/iso.png";
import fsscImg from "../../../assets/Images/certificates/fssc.png";
import brcImg from "../../../assets/Images/certificates/brc.png";
import nfplImg from "../../../assets/Images/certificates/nfpl.png";
import usfdaImg from "../../../assets/Images/certificates/usfda-1-11zon.png";

import 'swiper/css';

const Sixth_Aboutus_Section = () => {
  const swiperRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      id: 1,
      src: fssaiImg,
      alt: 'FSSAI Certificate'
    },
    {
      id: 2,
      src: haccpImg,
      alt: 'HACCP Certificate'
    },
    {
      id: 3,
      src: halalImg,
      alt: 'HALAL Certificate'
    },
    {
      id: 4,
      src: isoImg,
      alt: 'ISO Certificate'
    },
    {
      id: 5,
      src: fsscImg,
      alt: 'FSSC Certificate'
    },
    {
      id: 6,
      src: brcImg,
      alt: 'BRCGS Certificate'
    },
    {
      id: 7,
      src: nfplImg,
      alt: 'GMP Certificate'
    },
    {
      id: 8,
      src: usfdaImg,
      alt: 'USFDA Certificate'
    }
  ];

  const goPrev = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev();
    }
  };

  const goNext = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext();
    }
  };

  return (
    <section className="w-full bg-white py-16 overflow-hidden">

      <style>{`
        .certificate-swiper {
          overflow: hidden !important;
        }

        .certificate-swiper .swiper-wrapper {
          align-items: stretch;
        }

        .certificate-card {
          transition: all 0.4s ease;
        }

        .certificate-card:hover {
          transform: translateY(-6px);
        }

        .certificate-card img {
          transition: transform 0.5s ease;
        }

        .certificate-card:hover img {
          transform: scale(1.04);
        }

        .gradient-text {
          background: linear-gradient(135deg, #355931 0%, #A4BE2C 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Heading */}
        <div className="text-center mb-14">

          <p className="text-[#A4BE2C] tracking-[0.35em] text-[11px] font-semibold mb-4 uppercase">
            QUALITY & CERTIFICATION
          </p>

          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-serif text-[#355931] leading-tight">
            Our Commitment To <span className="gradient-text"> Safety & Quality
            </span>
          </h2>

          {/* Underline */}
          <div
            className="w-24 h-[2px] mx-auto mt-6 mb-6"
            style={{
              background:
                "linear-gradient(90deg, transparent 0%, #355931 30%, #A4BE2C 70%, transparent 100%)",
            }}
          />

          <p className="max-w-2xl mx-auto text-gray-600 text-sm sm:text-base leading-relaxed">
            Certified manufacturing standards that ensure premium quality,
            safety, and global compliance across every Nutralike product.
          </p>

        </div>

        {/* Slider */}
        <div className="relative">

          {/* Left Arrow */}
          <button
            onClick={goPrev}
            className="
              absolute
              left-0
              top-1/2
              -translate-y-1/2
              z-20
              w-10 h-10
              sm:w-12 sm:h-12
              rounded-full
              bg-[#355931]
              text-white
              flex
              items-center
              justify-center
              hover:bg-[#A4BE2C]
              transition-all
              duration-300
            "
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          {/* Right Arrow */}
          <button
            onClick={goNext}
            className="
              absolute
              right-0
              top-1/2
              -translate-y-1/2
              z-20
              w-10 h-10
              sm:w-12 sm:h-12
              rounded-full
              bg-[#355931]
              text-white
              flex
              items-center
              justify-center
              hover:bg-[#A4BE2C]
              transition-all
              duration-300
            "
          >
            <ArrowRight className="w-5 h-5" />
          </button>

          {/* Swiper */}
          <div className="px-14 overflow-hidden">

            <Swiper
              modules={[Navigation, Autoplay]}
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
              }}
              onSlideChange={(swiper) => {
                setCurrentSlide(swiper.realIndex);
              }}
              slidesPerView={4}
              spaceBetween={24}
              loop={true}
              speed={400}
              autoplay={{
                delay: 800,
                disableOnInteraction: false,
                pauseOnMouseEnter: false,
              }}
              allowTouchMove={true}
              className="certificate-swiper"
              breakpoints={{
                0: {
                  slidesPerView: 1,
                },
                640: {
                  slidesPerView: 2,
                },
                1024: {
                  slidesPerView: 4,
                },
              }}
            >

              {slides.map((slide) => (
                <SwiperSlide key={slide.id}>

                  <div className="certificate-card h-full">

                    <div className="
                      bg-white
                      rounded-3xl
                      border
                      border-[#E4E9DE]
                      overflow-hidden
                      p-4
                      shadow-sm
                      h-full
                    ">

                      <div className="
                        bg-[#F7F9F5]
                        rounded-2xl
                        overflow-hidden
                        aspect-[3/4]
                        flex
                        items-center
                        justify-center
                      ">

                        <img
                          src={slide.src}
                          alt={slide.alt}
                          className="
                            w-full
                            h-full
                            object-cover
                          "
                        />

                      </div>

                      <div className="pt-4 text-center">

                        <h3 className="
                          text-sm
                          sm:text-base
                          font-semibold
                          text-[#355931]
                          tracking-wide
                        ">
                          {slide.alt}
                        </h3>

                      </div>

                    </div>

                  </div>

                </SwiperSlide>
              ))}

            </Swiper>

          </div>

        </div>

        {/* Dots */}
        <div className="flex justify-center mt-10 gap-2">

          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => swiperRef.current?.slideToLoop(index)}
              className={`
                transition-all duration-300 rounded-full
                ${currentSlide === index
                  ? 'w-8 h-2 bg-[#A4BE2C]'
                  : 'w-2 h-2 bg-[#355931] opacity-40'
                }
              `}
            />
          ))}

        </div>

      </div>
    </section>
  );
};

export default Sixth_Aboutus_Section;